// Phase 0085 — Team Monitor: tasks of direct reports.

import { useState } from 'react'
import { App, Badge, Button, Card, Col, DatePicker, Empty, Row, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import { taskApi, type TaskInstance, type TaskStatus } from '@/features/hr/taskExecutionApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const STATUS_COLORS: Record<TaskStatus, string> = {
  PENDING: 'default', IN_PROGRESS: 'blue', COMPLETED: 'cyan',
  VERIFIED: 'green', REJECTED: 'red', OVERDUE: 'volcano',
  BLOCKED: 'orange', SKIPPED: 'default', CANCELLED: 'default',
}

export default function TeamMonitorPage() {
  const { message } = App.useApp()
  const [onDate, setOnDate] = useState<Dayjs>(dayjs())
  const dateStr = onDate.format('YYYY-MM-DD')

  const { data: tasks = [], isLoading, refetch } = useQuery({
    queryKey: ['team-monitor', dateStr],
    queryFn: () => taskApi.teamTasks(dateStr),
  })

  const escalateMut = useMutation({
    mutationFn: () => taskApi.runEscalation(),
    onSuccess: (data: unknown) => {
      const d = data as { L1?: number; L2?: number; L3?: number; marked_overdue?: number }
      message.success(`Escalation swept — L1:${d.L1 ?? 0} L2:${d.L2 ?? 0} L3:${d.L3 ?? 0}, marked overdue: ${d.marked_overdue ?? 0}`)
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const generateMut = useMutation({
    mutationFn: () => taskApi.generate(dateStr),
    onSuccess: (data: unknown) => {
      const d = data as { instances_created: number }
      message.success(`Generated ${d.instances_created} task instances for ${dateStr}`)
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const stats = computeStats(tasks)

  const columns: ColumnsType<TaskInstance> = [
    { title: 'Task', dataIndex: 'title' },
    { title: 'Assignee', dataIndex: 'assigned_to_user_id', width: 200,
      render: (id: string | null) => id ? <Text code style={{ fontSize: 11 }}>{id.substring(0, 8)}…</Text> : <Tag color="red">UNASSIGNED</Tag> },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (s: TaskStatus, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLORS[s]}>{s}</Tag>
          {r.escalation_level > 0 && <Tag color="red">Esc L{r.escalation_level}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Due',
      dataIndex: 'due_at',
      width: 130,
      render: (d: string | null) => d ? dayjs(d).format('MMM D HH:mm') : '—',
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      width: 130,
      render: (d: string | null) => d ? dayjs(d).format('HH:mm') : '—',
    },
  ]

  return (
    <>
      <PageHeader
        title="Team Monitor"
        subtitle="All tasks of your direct reports"
        extra={
          <Space>
            <DatePicker value={onDate} onChange={(d) => d && setOnDate(d)} />
            <Button onClick={() => generateMut.mutate()} loading={generateMut.isPending}>
              Generate Today
            </Button>
            <Button onClick={() => escalateMut.mutate()} loading={escalateMut.isPending}>
              Run Escalation Sweep
            </Button>
            <Button onClick={() => refetch()}>Refresh</Button>
          </Space>
        }
      />

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col span={4}><Card size="small"><Text type="secondary">Total</Text><div style={{ fontSize: 20, fontWeight: 600 }}>{tasks.length}</div></Card></Col>
        <Col span={4}><Card size="small"><Text type="secondary">Pending</Text><div style={{ fontSize: 20, fontWeight: 600 }}>{stats.pending}</div></Card></Col>
        <Col span={4}><Card size="small"><Text type="secondary">Verified</Text><div style={{ fontSize: 20, fontWeight: 600, color: '#52c41a' }}>{stats.verified}</div></Card></Col>
        <Col span={4}><Card size="small"><Text type="secondary">Overdue</Text><div style={{ fontSize: 20, fontWeight: 600, color: '#cf1322' }}>{stats.overdue}</div></Card></Col>
        <Col span={4}><Card size="small"><Text type="secondary">Blocked</Text><div style={{ fontSize: 20, fontWeight: 600, color: '#fa8c16' }}>{stats.blocked}</div></Card></Col>
        <Col span={4}><Card size="small"><Text type="secondary">Escalated</Text><div style={{ fontSize: 20, fontWeight: 600, color: '#cf1322' }}>{stats.escalated}</div></Card></Col>
      </Row>

      {tasks.length === 0 ? (
        <Empty description="No team tasks for this date. Either you have no direct reports configured, or generation hasn't run." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={tasks}
               loading={isLoading} pagination={{ pageSize: 50 }} />
      )}
    </>
  )
}

function computeStats(tasks: TaskInstance[]) {
  const s = { pending: 0, verified: 0, overdue: 0, blocked: 0, escalated: 0 }
  for (const t of tasks) {
    if (t.status === 'PENDING' || t.status === 'IN_PROGRESS') s.pending++
    if (t.status === 'VERIFIED') s.verified++
    if (t.status === 'OVERDUE') s.overdue++
    if (t.status === 'BLOCKED') s.blocked++
    if (t.escalation_level > 0) s.escalated++
  }
  return s
}
